import { Component } from '@angular/core';
import { CardComponent } from '@shared/components/card/card.component';
import { ModalService } from '@app/core/services/modal.service';
import { HeaderService } from '@app/core/services/header.service';
import { TableComponent } from '@shared/components/table/table.component';
import { TableConfig } from '@shared/interfaces/table/table-config';
import { ActivatedRoute, ParamMap } from '@angular/router';
import {
  MeteringPointCoreDataActionMenu
} from '@views/meteringpoint/meteringpoint-coredata/meteringpoint-coredata.action-menu';
import {
  SuggestCoredataModalComponent
} from '@shared/modals/meteringpoint/coredata/suggest-coredata-modal/suggest-coredata-modal.component';
import { AuthRoles, MeteringPointAdministrativePartyDto, MeteringPointDto } from '@app/api/dmc';
import { TranslatePipe } from '@ngx-translate/core';
import { ValueDetailsComponent } from '@shared/components/value-details/value-details.component';
import { contactInformationMasterData } from '@shared/interfaces/dmc/masterdata/contact-information.masterdata';
import { customerInformationMasterData } from '@shared/interfaces/dmc/masterdata/customer-information.masterdata';
import { Columns } from '@shared/interfaces/table/column-config';
import { StateManagerService } from '@shared/state/state-manager.service';
import { locationDetailsMasterData } from '@shared/interfaces/dmc/masterdata/location-details.masterdata';
import { meteringPointCoreDataColumns } from '@shared/table-configs/meteringpoint/meteringpoint-coredata.table';
import { hasRole } from '@app/core/guards/has-role.guard';

@Component({
  selector: 'app-meteringpoint-coredata',
  imports: [
    CardComponent,
    TableComponent,
    TranslatePipe,
    ValueDetailsComponent,
  ],
  templateUrl: './meteringpoint-coredata.component.html',
  styleUrl: './meteringpoint-coredata.component.css'
})
export class MeteringpointCoredataComponent {
  /** Selected Elements **/
  public selectedElements: MeteringPointAdministrativePartyDto[] = [];

  /** Data Value **/
  public locationMasterData: Columns<any>[] = locationDetailsMasterData.map(col => ({
    ...col,
    key: col.key.replace('installationLocationAddress.', 'locationAddress.')
  }));

  public contactMasterData: Columns<MeteringPointAdministrativePartyDto>[] = contactInformationMasterData;
  public customerMasterData: Columns<any>[] = customerInformationMasterData;

  /** Table Config **/
  public coreDataTableConfig: TableConfig<MeteringPointAdministrativePartyDto> = {
    ...meteringPointCoreDataColumns,
    refreshable: true,
    refresh: () => this.fetchData(),
  };

  /** Table Data **/
  public coreDataTable: any[] = [];

  /** Customer Data **/
  public customerData: any = {};

  /** Meteringpoint **/
  public meteringPoint?: MeteringPointDto;

  /** ID **/
  public id?: string;

  constructor(
    private readonly headerMenuService: HeaderService,
    private readonly modalService: ModalService,
    private readonly route: ActivatedRoute,
    private readonly stateManagerService: StateManagerService,
  ) {
    this.route.parent?.paramMap.subscribe((q: ParamMap) => {
      this.id = q.get('id')!;

      this.fetchData();
    });

    const mpWriter = hasRole([AuthRoles.MeteringPointWriter]);

    this.headerMenuService.setActions(MeteringPointCoreDataActionMenu,
      {
        suggest: () => this.openSuggestModal(),
      },
      {
        suggest: () => !mpWriter
      }
    )
  }

  public fetchData(): void {
    if (!this.id) return;

    this.stateManagerService.getMeteringPoint(this.id).subscribe((data) => {
      // Populate core data table
      this.populateCoreDataTable(data);

      // Sort by relationType
      this.sortCoreDataTable();

      // Set judicial address as selected if it exists
      this.selectJudicialAddressIfExists();

      // Define customer data
      this.customerData = {
        customer1: data.detailMeteringPointPartyCharacteristic?.firstConsumerParty?.name ?? '',
        cvr1: data.detailMeteringPointPartyCharacteristic?.firstConsumerParty?.cvr ?? null,
        customer2: data.detailMeteringPointPartyCharacteristic?.secondConsumerParty?.name ?? '',
        cvr2: data.detailMeteringPointPartyCharacteristic?.secondConsumerParty?.cvr ?? null,
        protectedAddress: data.detailMeteringPointPartyCharacteristic?.protectedName ?? false
      };

      // Set metering point
      this.meteringPoint = data;
    });
  }

  private populateCoreDataTable(data: any): void {
    this.coreDataTable = [];

    data.detailMeteringPointPartyCharacteristic?.meteringPointAdministrativeParty?.forEach((meteringPoint: MeteringPointDto) => {
      this.coreDataTable.push({
        ...meteringPoint,
        validFrom: data.detailMeteringPointPartyCharacteristic?.validFrom,
        validTo: data.detailMeteringPointPartyCharacteristic?.validTo,
        createdBy: data.detailMeteringPointPartyCharacteristic?.createdBy,
        modifiedBy: data.detailMeteringPointPartyCharacteristic?.modifiedBy,
      });
    });
  }

  private sortCoreDataTable(): void {
    this.coreDataTable.sort((a, b) => a.relationType.localeCompare(b.relationType));
  }

  private selectJudicialAddressIfExists(): void {
    const judicialAddress = this.coreDataTable.find(x => x.relationType === 'Judicialaddress');

    if (judicialAddress) {
      this.selectedElements = [judicialAddress];
    }
  }


  public openSuggestModal(): void {
    this.modalService.show({
      component: SuggestCoredataModalComponent,
      title: 'meteringpoint.coredata.suggest.title',
      size: 'lg',
      actions: [
        { type: 'primary', label: 'system.button.save', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: { id: this.id, meteringPoint: this.meteringPoint },
    }).subscribe(() => this.fetchData())
  }
}
